<template>
  <div class="lesson-content">
    <h1>Install Terraform</h1>
    <p>Terraform is an open-source infrastructure as code (IaC) tool by HashiCorp, enabling developers to safely and predictably manage, create, and version cloud infrastructure. Terraform lets you describe your entire cloud infrastructure as code, allowing automation and repeatability.</p>

    <h2>Step 1: Tap HashiCorp Repository</h2>
    <p>Homebrew uses "taps" to access external repositories that contain software. The <code>brew tap</code> command adds these external repositories.</p>
    <p>Run the following command to tap HashiCorp's official Homebrew tap:</p>
    <div class="code-block">
      <pre><code>brew tap hashicorp/tap</code></pre>
      <button class="copy-button" @click="copyTapCommand" title="Copy to clipboard">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>
      </button>
    </div>

    <h2>Step 2: Install Terraform</h2>
    <p>Now, install Terraform from the tapped repository using this command:</p>
    <div class="code-block">
      <pre><code>brew install hashicorp/tap/terraform</code></pre>
      <button class="copy-button" @click="copyInstallCommand" title="Copy to clipboard">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>
      </button>
    </div>

    <h2>Step 3: Verify Installation</h2>
    <p>To confirm that Terraform has been installed successfully, check the Terraform version:</p>
    <div class="code-block">
      <pre><code>terraform -version</code></pre>
      <button class="copy-button" @click="copyVersionCommand" title="Copy to clipboard">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>
      </button>
    </div>
    <p>You should see an output similar to:</p>
    <pre><code class="output">Terraform v1.8.3
on darwin_arm64</code></pre>

    <h2>Understanding Brew Tap</h2>
    <h3>What is brew tap?</h3>
    <p><code>brew tap</code> allows you to add additional repositories (also called taps) to your Homebrew installation. These repositories contain software formulas that might not be available in the default Homebrew core.</p>

    <h3>Why tap for Terraform?</h3>
    <p>The official HashiCorp tap (<code>hashicorp/tap</code>) provides the most up-to-date, officially maintained versions of Terraform and other HashiCorp tools.</p>

    <p class="note">Terraform is now ready to use on your Mac! You can proceed to set up your AWS infrastructure and other resources in subsequent lessons.</p>
  </div>
</template>

<script>
export default {
  name: 'Chapter1Lesson3',
  methods: {
    async copyToClipboard(text, button) {
      await navigator.clipboard.writeText(text);
      button.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';
      setTimeout(() => {
        button.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>';
      }, 2000);
    },
    copyTapCommand(event) {
      const command = 'brew tap hashicorp/tap';
      this.copyToClipboard(command, event.currentTarget);
    },
    copyInstallCommand(event) {
      const command = 'brew install hashicorp/tap/terraform';
      this.copyToClipboard(command, event.currentTarget);
    },
    copyVersionCommand(event) {
      const command = 'terraform -version';
      this.copyToClipboard(command, event.currentTarget);
    }
  }
}
</script>

<style scoped>
.lesson-content {
  color: var(--text);
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

h1 {
  color: var(--primary);
  font-size: 2rem;
  margin-bottom: 1.5rem;
  font-weight: 600;
}

h2 {
  color: var(--text);
  font-size: 1.5rem;
  margin: 2rem 0 1rem;
  font-weight: 600;
}

h3 {
  color: var(--text);
  font-size: 1.25rem;
  margin: 1.5rem 0 0.75rem;
  font-weight: 600;
}

p {
  margin-bottom: 1rem;
}

ul, ol {
  margin: 1rem 0;
  padding-left: 1.5rem;
}

li {
  margin-bottom: 0.5rem;
}

pre {
  background: #f8fafc;
  border-radius: 6px;
  padding: 1rem;
  margin: 1rem 0;
  overflow-x: auto;
  border: 1px solid var(--border);
}

code {
  font-family: 'Fira Code', 'Consolas', monospace;
  font-size: 0.9rem;
}

p code {
  background: #f8fafc;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-size: 0.9rem;
}

.note {
  background-color: #f8fafc;
  border-left: 4px solid var(--primary);
  padding: 1rem;
  margin: 1rem 0;
  font-style: italic;
}

code.output {
  color: #16a34a;  /* Green color for output text */
}

.code-block {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.code-block pre {
  flex-grow: 1;
  margin-right: 0.5rem;
}

.copy-button {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  color: var(--text);
  opacity: 0.7;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.copy-button:hover {
  opacity: 1;
}

.copy-button svg {
  width: 20px;
  height: 20px;
}
</style> 